import React from 'react';
import { Link, NavLink, Outlet, matchPath, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Badge, Button, Modal } from '@/components/ui';
import { BranchFilterSelect } from '@/components/common/BranchFilterSelect';
import { ChevronDown, ChevronRight, Info, LogOut, Menu, RefreshCw, UserCircle, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';

type PageHint = {
  pattern: string;
  title: string;
  description: string;
  actions: string[];
};

type MenuItem = {
  label: string;
  href: string;
  icon: string;
  permission?: string | null;
  permissions?: string[]; // Any of these permissions grants access
  role?: string;
};

type MenuGroup = {
  label: string;
  icon: string;
  items: MenuItem[];
};

type MenuEntry = { type: 'item'; item: MenuItem } | { type: 'group'; group: MenuGroup };

const pageHints: PageHint[] = [
  {
    pattern: '/',
    title: 'Панель управления',
    description: 'Это главный экран системы. Здесь вы быстро видите текущую ситуацию по посещаемости, устройствам и ключевым показателям.',
    actions: [
      'Проверять сводку по сотрудникам, событиям и активности филиалов.',
      'Быстро замечать проблемные зоны: опоздания, отсутствие отметок и офлайн-устройства.',
      'Переходить в нужный раздел для детальной работы.',
    ],
  },
  {
    pattern: '/dashboard',
    title: 'Панель управления',
    description: 'Это главный экран системы. Здесь вы быстро видите текущую ситуацию по посещаемости, устройствам и ключевым показателям.',
    actions: [
      'Проверять сводку по сотрудникам, событиям и активности филиалов.',
      'Быстро замечать проблемные зоны: опоздания, отсутствие отметок и офлайн-устройства.',
      'Переходить в нужный раздел для детальной работы.',
    ],
  },
  {
    pattern: '/employees',
    title: 'Сотрудники',
    description: 'В этом разделе хранится список сотрудников компании. Здесь удобно вести кадровую базу и готовить сотрудников к работе с системой учета.',
    actions: [
      'Добавлять, редактировать и деактивировать сотрудников.',
      'Фильтровать сотрудников по филиалу, отделу и поиску.',
      'Переходить в карточку сотрудника для просмотра деталей и привязок.',
    ],
  },
  {
    pattern: '/employees/:id',
    title: 'Карточка сотрудника',
    description: 'Это детальная карточка конкретного сотрудника. Здесь собрана информация, связанная с его профилем, посещаемостью и привязкой к устройствам.',
    actions: [
      'Просматривать основные данные сотрудника.',
      'Проверять привязки к терминалам и идентификаторы на устройстве.',
      'Анализировать посещаемость, штрафы и связанные записи.',
    ],
  },
  {
    pattern: '/devices',
    title: 'Устройства',
    description: 'Здесь вы управляете терминалами Hikvision. Раздел предназначен для настройки точек учета и контроля синхронизации событий.',
    actions: [
      'Добавлять и редактировать терминалы Hikvision.',
      'Проверять IP, модель, логин и параметры подключения.',
      'Открывать карточку устройства для теста связи и ручной синхронизации.',
    ],
  },
  {
    pattern: '/devices/:id',
    title: 'Карточка устройства',
    description: 'Это экран управления конкретным терминалом Hikvision. Здесь собраны настройки подключения, синхронизация событий и служебная информация.',
    actions: [
      'Тестировать соединение с устройством.',
      'Запускать ручную синхронизацию событий из терминала.',
      'Проверять сотрудников, IP allowlist и последние ошибки устройства.',
    ],
  },
  {
    pattern: '/timesheets',
    title: 'Табели',
    description: 'В этом разделе отображаются табели рабочего времени. Он нужен для анализа отработанных часов и итогового статуса сотрудника за день.',
    actions: [
      'Смотреть входы, выходы, опоздания и неявки.',
      'Фильтровать табели по сотруднику и периоду.',
      'Проверять данные перед расчетом штрафов и зарплаты.',
    ],
  },
  {
    pattern: '/attendance-events',
    title: 'События посещаемости',
    description: 'Здесь находятся сырые события, полученные с терминалов. Это основной технический раздел для проверки того, что пришло с устройства.',
    actions: [
      'Просматривать события входа и выхода по сотрудникам и устройствам.',
      'Проверять тип верификации: лицо, карта, отпечаток и другие методы.',
      'Искать необработанные или неподвязанные события.',
    ],
  },
  {
    pattern: '/operations',
    title: 'Операции',
    description: 'Раздел для оперативной работы по сменам и активному персоналу. Он помогает быстро понимать, кто сейчас должен быть на месте.',
    actions: [
      'Проверять активный персонал по времени и филиалу.',
      'Смотреть недельный план и рабочие назначения.',
      'Быстро реагировать на отклонения в расписании.',
    ],
  },
  {
    pattern: '/shifts',
    title: 'Смены',
    description: 'Здесь настраиваются шаблоны рабочих смен. Эти данные используются в табелях, штрафах и расчете заработной платы.',
    actions: [
      'Создавать стандартные смены для филиалов и отделов.',
      'Настраивать время начала, окончания и правила опозданий.',
      'Поддерживать единый порядок расчета рабочего времени.',
    ],
  },
  {
    pattern: '/shift-assignments',
    title: 'Назначения смен',
    description: 'Раздел отвечает за распределение смен между сотрудниками. Здесь формируется фактический график работы.',
    actions: [
      'Назначать сотрудникам смены на нужные даты.',
      'Просматривать график в разрезе периода.',
      'Подготавливать расписание для операций и табелей.',
    ],
  },
  {
    pattern: '/employee-day-offs',
    title: 'Личные выходные',
    description: 'Раздел для персональных выходных сотрудников с причиной. Эти даты отключают ожидание отметки Face ID в конкретный день.',
    actions: [
      'Добавлять персональные выходные по диапазону дат и дням недели.',
      'Редактировать причину и тип оплаты выходного.',
      'Поддерживать актуальный график без ручных правок табеля.',
    ],
  },
  {
    pattern: '/shift-change-requests',
    title: 'Обмен и замены смен',
    description: 'Этот раздел нужен для оформления замены сотрудника на конкретную дату или официального обмена сменами между двумя сотрудниками.',
    actions: [
      'Создавать заявки на подмену при больничном, отпуске или другом отсутствии.',
      'Фиксировать обмен сменами до начала рабочего дня и до появления отметок.',
      'Одобрять или отклонять запросы, чтобы табели и графики пересчитывались корректно.',
    ],
  },
  {
    pattern: '/holidays',
    title: 'Праздники',
    description: 'Здесь ведется календарь праздничных и нерабочих дней. Эти даты влияют на табели и итоговые расчеты.',
    actions: [
      'Добавлять официальные и внутренние выходные дни.',
      'Учитывать особые даты при расчете присутствия.',
      'Поддерживать корректность графиков и табелей.',
    ],
  },
  {
    pattern: '/penalties',
    title: 'Штрафы',
    description: 'В этом разделе отображаются уже назначенные штрафы сотрудникам. Здесь собраны как автоматические начисления по правилам, так и ручные штрафы, выбранные из типов штрафов.',
    actions: [
      'Просматривать фактически начисленные штрафы по сотрудникам и датам.',
      'Подтверждать, отклонять и контролировать статусы удержаний.',
      'Назначать ручные штрафы на основе заранее созданных типов штрафов.',
    ],
  },
  {
    pattern: '/penalty-presets',
    title: 'Типы штрафов',
    description: 'Здесь создаются типы ручных штрафов. Эти записи используются в форме назначения штрафа и не заменяют правила автоматического расчета.',
    actions: [
      'Создавать шаблоны ручных штрафов с суммой, кодом и описанием.',
      'Ограничивать типы штрафов конкретным филиалом или делать их общими.',
      'Поддерживать список причин, которые HR выбирает при ручном начислении штрафов.',
    ],
  },
  {
    pattern: '/penalty-rules',
    title: 'Правила штрафов',
    description: 'Раздел содержит правила автоматического расчета штрафов по табелю, опозданиям и другим нарушениям. Это не список ручных типов штрафов, а логика автоматических начислений.',
    actions: [
      'Настраивать суммы за опоздание, отсутствие и другие случаи.',
      'Поддерживать единые правила для филиалов.',
      'Корректировать правила при изменении политики компании.',
    ],
  },
  {
    pattern: '/payroll/runs',
    title: 'Расчеты зарплаты',
    description: 'Здесь создаются и ведутся расчетные периоды по зарплате. Раздел объединяет табели, штрафы и надбавки в один цикл расчета.',
    actions: [
      'Создавать новые зарплатные расчеты по периоду.',
      'Запускать генерацию начислений и контролировать статус.',
      'Переходить в детальный расчет для проверки ведомости.',
    ],
  },
  {
    pattern: '/payroll/runs/:id',
    title: 'Детали расчета зарплаты',
    description: 'Это карточка конкретного расчетного периода. Здесь удобно проверять результат начислений перед финализацией.',
    actions: [
      'Просматривать данные по сотрудникам и начислениям.',
      'Проверять суммы, удержания и статус расчета.',
      'Подготавливать расчет к закрытию или пересчету.',
    ],
  },
  {
    pattern: '/payroll/profiles',
    title: 'Профили зарплаты',
    description: 'В разделе хранятся зарплатные профили сотрудников. Эти настройки определяют базовые параметры начислений.',
    actions: [
      'Назначать сотрудникам тип оплаты и базовые суммы.',
      'Хранить банковские и расчетные данные.',
      'Подготавливать сотрудников к корректному расчету зарплаты.',
    ],
  },
  {
    pattern: '/payroll/leaves',
    title: 'Отпуска',
    description: 'Здесь ведется учет отпусков и заявок сотрудников. Раздел помогает контролировать отсутствие по уважительным причинам.',
    actions: [
      'Создавать и просматривать заявки на отпуск.',
      'Проверять статусы одобрения и даты отсутствия.',
      'Учитывать отпуска в графиках и зарплате.',
    ],
  },
  {
    pattern: '/payroll/salary-components',
    title: 'Компоненты зарплаты',
    description: 'В этом разделе задаются элементы начислений и удержаний. Они используются как строительные блоки зарплатной модели.',
    actions: [
      'Создавать надбавки, бонусы и удержания.',
      'Разделять постоянные и разовые компоненты.',
      'Поддерживать прозрачную структуру расчетов.',
    ],
  },
  {
    pattern: '/reports',
    title: 'Отчеты',
    description: 'Раздел отчетов нужен для анализа данных системы. Здесь можно получить сводную информацию по посещаемости, зарплате и активности.',
    actions: [
      'Формировать ежедневные и периодические отчеты.',
      'Подготавливать выгрузки для руководства и бухгалтерии.',
      'Сравнивать показатели по филиалам и сотрудникам.',
    ],
  },
  {
    pattern: '/profile',
    title: 'Профиль',
    description: 'Это личный раздел текущего пользователя. Здесь сосредоточены персональные данные и настройки учетной записи.',
    actions: [
      'Проверять данные своей учетной записи.',
      'Контролировать роль и доступы в системе.',
      'Использовать раздел как личную справочную карточку.',
    ],
  },
  {
    pattern: '/organizations',
    title: 'Организации',
    description: 'Раздел доступен супер-администратору и нужен для работы в multi-tenant режиме. Здесь задаются верхнеуровневые организации.',
    actions: [
      'Создавать и редактировать организации.',
      'Разделять данные между разными компаниями.',
      'Контролировать структуру арендаторов системы.',
    ],
  },
  {
    pattern: '/branches',
    title: 'Филиалы',
    description: 'В этом разделе ведется список филиалов компании. Филиалы используются как основа для сотрудников, смен, устройств и отчетов.',
    actions: [
      'Добавлять и редактировать филиалы.',
      'Указывать коды, адреса и принадлежность к организации.',
      'Использовать филиалы для фильтрации всей операционной работы.',
    ],
  },
  {
    pattern: '/departments',
    title: 'Отделы',
    description: 'Здесь настраивается организационная структура внутри филиалов. Отделы помогают точнее группировать сотрудников и процессы.',
    actions: [
      'Создавать отделы и связывать их с филиалами.',
      'Упорядочивать сотрудников по подразделениям.',
      'Использовать отделы в фильтрах и аналитике.',
    ],
  },
  {
    pattern: '/users',
    title: 'Пользователи',
    description: 'Раздел предназначен для системных пользователей, которые входят в CRM. Здесь настраивается доступ сотрудников и администраторов.',
    actions: [
      'Создавать учетные записи и управлять ими.',
      'Привязывать пользователей к организациям и филиалам.',
      'Контролировать, кто имеет доступ к системе.',
    ],
  },
  {
    pattern: '/roles',
    title: 'Роли',
    description: 'В этом разделе управляются роли и права доступа. Он нужен для безопасного разграничения функций пользователей.',
    actions: [
      'Просматривать роли и наборы разрешений.',
      'Поддерживать корректную модель доступа.',
      'Разделять полномочия между HR, администраторами и руководителями.',
    ],
  },
  {
    pattern: '/audit-logs',
    title: 'Журнал аудита',
    description: 'Здесь сохраняется история действий в системе. Раздел нужен для контроля изменений и расследования спорных ситуаций.',
    actions: [
      'Просматривать, кто и когда внес изменения.',
      'Проверять действия пользователей по важным объектам.',
      'Использовать журнал для внутреннего контроля и безопасности.',
    ],
  },
  {
    pattern: '/admin/telegram-links',
    title: 'Telegram',
    description: 'Управление Telegram-ботом. Просматривайте подключенных сотрудников, отправляйте тестовые сообщения и массовые уведомления.',
    actions: [
      'Просматривать список ulangan сотрудников и их активность.',
      'Отправлять test xabar или ommaviy broadcast.',
      'Otklyuchat\' ulangan akkauntlarni admin tomonidan.',
    ],
  },
];

export default function MainLayout() {
  const { user, logout, can, hasRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [sidebarOpen, setSidebarOpen] = React.useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [hintOpen, setHintOpen] = React.useState(false);
  const [lastRefreshed, setLastRefreshed] = React.useState<Date>(new Date());
  const [refreshing, setRefreshing] = React.useState(false);

  const handleRefreshAll = React.useCallback(async () => {
    setRefreshing(true);
    await queryClient.invalidateQueries();
    setLastRefreshed(new Date());
    setRefreshing(false);
  }, [queryClient]);

  // Track when queries are automatically refetched (every 30s).
  React.useEffect(() => {
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (event.type === 'updated' && event.action?.type === 'success') {
        setLastRefreshed(new Date());
      }
    });
    return unsubscribe;
  }, [queryClient]);
  const [collapsedPopover, setCollapsedPopover] = React.useState<{
    label: string;
    items: MenuItem[];
    left: number;
    top: number;
  } | null>(null);
  const [collapsedHint, setCollapsedHint] = React.useState<{
    label: string;
    left: number;
    top: number;
  } | null>(null);
  const sidebarRef = React.useRef<HTMLDivElement | null>(null);
  const collapsedPopoverRef = React.useRef<HTMLDivElement | null>(null);

  const currentHint = React.useMemo(() => {
    return pageHints.find((hint) =>
      matchPath({ path: hint.pattern, end: true }, location.pathname)
    );
  }, [location.pathname]);

  const currentTitle = currentHint?.title ?? 'HR HAPPYTEL';

  React.useEffect(() => {
    setHintOpen(false);
    setMobileMenuOpen(false);
    setCollapsedPopover(null);
    setCollapsedHint(null);
  }, [location.pathname]);

  React.useEffect(() => {
    if (sidebarOpen) {
      setCollapsedPopover(null);
      setCollapsedHint(null);
    }
  }, [sidebarOpen]);

  React.useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        !sidebarRef.current?.contains(target) &&
        !collapsedPopoverRef.current?.contains(target)
      ) {
        setCollapsedPopover(null);
        setCollapsedHint(null);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, []);

  const handleLogout = async () => {
    const confirmed = window.confirm('Вы уверены, что хотите выйти из системы?');
    if (!confirmed) {
      return;
    }

    await logout();
    toast.success('Вы успешно вышли из системы');
    navigate('/login');
  };

  const menuStructure: MenuEntry[] = [
    {
      type: 'item',
      item: { label: 'Панель', href: '/dashboard', icon: '📊', permission: null },
    },
    {
      type: 'item',
      item: { label: 'Сотрудники', href: '/employees', icon: '👥', permission: 'manage_employees' },
    },
    {
      type: 'item',
      item: { label: 'Устройства', href: '/devices', icon: '🔧', permission: 'manage_devices' },
    },
    {
      type: 'group',
      group: {
        label: 'Посещаемость',
        icon: '📋',
        items: [
          { label: 'Табели', href: '/timesheets', icon: '📋', permission: 'view_attendance' },
          { label: 'События', href: '/attendance-events', icon: '📡', permission: 'view_attendance' },
          { label: 'Операции', href: '/operations', icon: '🧭', permission: 'view_attendance' },
        ],
      },
    },
    {
      type: 'group',
      group: {
        label: 'Смены',
        icon: '⏰',
        items: [
          { label: 'Смены', href: '/shifts', icon: '⏰', permission: 'manage_shifts' },
          { label: 'Назначения смен', href: '/shift-assignments', icon: '📆', permission: 'manage_shifts' },
          { label: 'Личные выходные', href: '/employee-day-offs', icon: '🛌', permission: 'manage_shifts' },
          { label: 'Обмен и замены', href: '/shift-change-requests', icon: '🔁', permission: 'manage_shifts' },
          { label: 'Праздники', href: '/holidays', icon: '🎉', permission: 'manage_holidays' },
        ],
      },
    },
    {
      type: 'group',
      group: {
        label: 'Штрафы',
        icon: '⚠️',
        items: [
          { label: 'Штрафы', href: '/penalties', icon: '⚠️', permissions: ['manage_penalties', 'approve_penalties'] },
          { label: 'Типы штрафов', href: '/penalty-presets', icon: '🧾', permission: 'manage_penalties' },
          { label: 'Правила штрафов', href: '/penalty-rules', icon: '⚖️', permission: 'manage_penalties' },
        ],
      },
    },
    {
      type: 'group',
      group: {
        label: 'Зарплата',
        icon: '💰',
        items: [
          { label: 'Расчеты ЗП', href: '/payroll/runs', icon: '💰', permissions: ['view_payroll', 'run_payroll'] },
          { label: 'Профили ЗП', href: '/payroll/profiles', icon: '💳', permission: 'manage_payroll' },
          { label: 'Отпуска', href: '/payroll/leaves', icon: '🏖️', permissions: ['manage_leaves', 'approve_leaves'] },
          { label: 'Компоненты ЗП', href: '/payroll/salary-components', icon: '📦', permission: 'manage_payroll' },
        ],
      },
    },
    {
      type: 'item',
      item: { label: 'Отчеты', href: '/reports', icon: '📈', permissions: ['view_attendance', 'view_payroll'] },
    },
    {
      type: 'group',
      group: {
        label: 'Администрирование',
        icon: '🔧',
        items: [
          { label: 'Организации', href: '/organizations', icon: '🏛️', role: 'super_admin' },
          { label: 'Филиалы', href: '/branches', icon: '🏢', permission: 'manage_branches' },
          { label: 'Отделы', href: '/departments', icon: '🏛️', permission: 'manage_departments' },
          { label: 'Пользователи', href: '/users', icon: '👤', permission: 'manage_users' },
          { label: 'Роли', href: '/roles', icon: '🔐', permission: 'manage_roles' },
          { label: 'Telegram', href: '/admin/telegram-links', icon: '📨', permission: 'manage_telegram' },
          { label: 'Журнал аудита', href: '/audit-logs', icon: '📜', role: 'super_admin' },
        ],
      },
    },
  ];

  const canSeeItem = (item: MenuItem) => {
    if (!item.permission && !item.role && !item.permissions) return true;
    if (item.role) return hasRole(item.role);
    if (item.permissions) return item.permissions.some((p) => can(p));
    if (item.permission) return can(item.permission);
    return false;
  };

  const [openGroups, setOpenGroups] = React.useState<Record<string, boolean>>(() => {
    // Auto-open the group that contains the current path
    const initial: Record<string, boolean> = {};
    menuStructure.forEach((entry) => {
      if (entry.type === 'group') {
        const active = entry.group.items.some((i) => location.pathname.startsWith(i.href));
        if (active) initial[entry.group.label] = true;
      }
    });
    return initial;
  });

  const toggleGroup = (label: string) =>
    setOpenGroups((prev) => {
      const isOpen = !!prev[label];
      // Boshqa barcha grouplarni yopib, faqat bosilganini toggle qilish
      const next: Record<string, boolean> = {};
      if (!isOpen) next[label] = true;
      return next;
    });

  const showSidebarLabels = sidebarOpen || mobileMenuOpen;

  const getFloatingPanelPosition = React.useCallback(
    (trigger: HTMLElement, estimatedHeight?: number) => {
      const triggerRect = trigger.getBoundingClientRect();
      const sidebarRect = sidebarRef.current?.getBoundingClientRect();
      const left = (sidebarRect?.right ?? triggerRect.right) + 12;

      if (!estimatedHeight) {
        return {
          left,
          top: triggerRect.top + triggerRect.height / 2,
        };
      }

      return {
        left,
        top: Math.min(
          Math.max(12, triggerRect.top - 8),
          Math.max(12, window.innerHeight - estimatedHeight - 12)
        ),
      };
    },
    []
  );

  const showCollapsedHint = React.useCallback(
    (label: string, trigger: HTMLElement) => {
      if (showSidebarLabels || collapsedPopover) {
        return;
      }

      setCollapsedHint({
        label,
        ...getFloatingPanelPosition(trigger),
      });
    },
    [collapsedPopover, getFloatingPanelPosition, showSidebarLabels]
  );

  const hideCollapsedHint = React.useCallback(() => {
    setCollapsedHint(null);
  }, []);

  const toggleCollapsedPopover = React.useCallback(
    (group: MenuGroup, items: MenuItem[], trigger: HTMLElement) => {
      if (showSidebarLabels) {
        return;
      }

      setCollapsedHint(null);
      setCollapsedPopover((previous) => {
        if (previous?.label === group.label) {
          return null;
        }

        return {
          label: group.label,
          items,
          ...getFloatingPanelPosition(trigger, Math.max(180, items.length * 48 + 72)),
        };
      });
    },
    [getFloatingPanelPosition, showSidebarLabels]
  );

  const visibleMenuItems = React.useMemo(() => {
    return menuStructure.flatMap((entry) => {
      if (entry.type === 'item') {
        return canSeeItem(entry.item) ? [entry.item] : [];
      }

      return entry.group.items.filter(canSeeItem);
    });
  }, [can, hasRole, location.pathname]);

  const breadcrumbs = React.useMemo(() => {
    const items: Array<{ label: string; href?: string }> = [];
    const currentLabel = currentHint?.title || currentTitle;
    const activeItem = visibleMenuItems
      .filter((item) => item.href !== '/' && location.pathname.startsWith(item.href))
      .sort((left, right) => right.href.length - left.href.length)[0];

    if (!['/', '/dashboard'].includes(location.pathname)) {
      items.push({ label: 'Панель', href: '/dashboard' });
    }

    if (activeItem && activeItem.href !== location.pathname && activeItem.href !== '/dashboard') {
      items.push({ label: activeItem.label, href: activeItem.href });
    }

    if (!items.length || items[items.length - 1]?.label !== currentLabel) {
      items.push({ label: currentLabel });
    }

    return items;
  }, [currentHint?.title, currentTitle, location.pathname, visibleMenuItems]);
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div className="flex min-h-screen bg-transparent">
      {mobileMenuOpen && (
        <button
          aria-label="Close menu"
          className="fixed inset-0 z-30 bg-black/45 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        ref={sidebarRef}
        className={`fixed inset-y-0 left-0 z-40 h-screen bg-slate-950 text-white transition-transform duration-300 md:sticky md:top-0 md:z-auto md:translate-x-0 ${
          mobileMenuOpen ? 'translate-x-0 w-[19rem] max-w-[85vw]' : '-translate-x-full w-[19rem] max-w-[85vw]'
        } ${sidebarOpen ? 'md:w-64' : 'md:w-20'} md:flex md:flex-col`}
      >
        <div className="p-4 flex items-center justify-between border-b border-gray-800">
          <div className={`flex items-center ${sidebarOpen ? 'gap-2.5' : ''}`}>
            <img
              src="/logoIcon.svg"
              alt="HR HAPPYTEL logo"
              className="h-7 w-7 rounded-md object-contain ring-1 ring-white/15"
            />
            {sidebarOpen && <h1 className="font-bold text-xl tracking-tight">HR HAPPYTEL</h1>}
          </div>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="hidden hover:bg-gray-800 p-2 rounded md:inline-flex"
          >
            <Menu size={20} />
          </button>
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="hover:bg-gray-800 p-2 rounded md:hidden"
          >
            <X size={20} />
          </button>
        </div>

        <nav className="flex-1 space-y-0.5 overflow-x-visible overflow-y-auto p-2 scrollbar-thin scrollbar-thumb-gray-700">
          {menuStructure.map((entry) => {
            if (entry.type === 'item') {
              if (!canSeeItem(entry.item)) return null;
              return (
                <NavLink
                  key={entry.item.href}
                  to={entry.item.href}
                  onClick={() => {
                    setMobileMenuOpen(false);
                    setCollapsedPopover(null);
                  }}
                  onMouseEnter={(event) => showCollapsedHint(entry.item.label, event.currentTarget)}
                  onMouseLeave={hideCollapsedHint}
                  onFocus={(event) => showCollapsedHint(entry.item.label, event.currentTarget)}
                  onBlur={hideCollapsedHint}
                  title={!showSidebarLabels ? entry.item.label : undefined}
                  className={({ isActive }) =>
                    `group flex items-center gap-3 rounded-lg px-3 py-2.5 transition-all duration-150 ${
                      isActive
                        ? 'bg-orange-500 text-white shadow-md'
                        : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                    } ${showSidebarLabels ? 'justify-start' : 'justify-center'}`
                  }
                >
                  <span className="text-lg shrink-0 leading-none">{entry.item.icon}</span>
                  {showSidebarLabels && (
                    <span className="text-sm font-medium truncate">{entry.item.label}</span>
                  )}
                </NavLink>
              );
            }

            // Group
            const { group } = entry;
            const visibleItems = group.items.filter(canSeeItem);
            if (visibleItems.length === 0) return null;
            const isGroupOpen = !!openGroups[group.label];
            const hasActiveChild = visibleItems.some((i) => location.pathname.startsWith(i.href));
            const isCollapsedPopoverOpen =
              !showSidebarLabels && collapsedPopover?.label === group.label;

            return (
              <div key={group.label} className={showSidebarLabels ? 'overflow-hidden' : undefined}>
                <button
                  onClick={(event) => {
                    if (showSidebarLabels) {
                      toggleGroup(group.label);
                      return;
                    }

                    toggleCollapsedPopover(group, visibleItems, event.currentTarget);
                  }}
                  onMouseEnter={(event) => showCollapsedHint(group.label, event.currentTarget)}
                  onMouseLeave={hideCollapsedHint}
                  onFocus={(event) => showCollapsedHint(group.label, event.currentTarget)}
                  onBlur={hideCollapsedHint}
                  title={!showSidebarLabels ? group.label : undefined}
                  className={`group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 transition-all duration-150 ${
                    hasActiveChild
                      ? 'bg-gray-700/80 text-white'
                      : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                  } ${showSidebarLabels ? 'justify-start' : 'justify-center'}`}
                >
                  <span className="text-lg shrink-0 leading-none">{group.icon}</span>
                  {showSidebarLabels ? (
                    <>
                      <span className="text-sm font-medium flex-1 text-left truncate">{group.label}</span>
                      <ChevronDown
                        size={15}
                        className={`shrink-0 text-gray-400 transition-transform duration-300 ${
                          isGroupOpen ? 'rotate-180' : 'rotate-0'
                        }`}
                      />
                    </>
                  ) : (
                    <ChevronDown
                      size={15}
                      className={`ml-auto shrink-0 text-gray-500 transition-transform duration-300 ${
                        isCollapsedPopoverOpen ? 'rotate-180' : 'rotate-[-90deg]'
                      }`}
                    />
                  )}
                </button>

                {/* Animated dropdown — max-height trick for smooth open/close */}
                <div
                  style={{
                    maxHeight: isGroupOpen && showSidebarLabels ? `${visibleItems.length * 44}px` : '0px',
                    opacity: isGroupOpen && showSidebarLabels ? 1 : 0,
                  }}
                  className="overflow-hidden transition-all duration-300 ease-in-out"
                >
                  <div className="ml-3 mt-1 mb-1 pl-3 border-l-2 border-indigo-500/40 space-y-0.5">
                    {visibleItems.map((item) => (
                      <NavLink
                        key={item.href}
                        to={item.href}
                        onClick={() => setMobileMenuOpen(false)}
                        className={({ isActive }) =>
                          `flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm transition-all duration-150 ${
                            isActive
                              ? 'bg-orange-500 text-white font-medium shadow'
                              : 'text-gray-400 hover:bg-gray-700/70 hover:text-white'
                          }`
                        }
                      >
                        <span className="text-base leading-none">{item.icon}</span>
                        <span className="truncate">{item.label}</span>
                      </NavLink>
                    ))}
                  </div>
                </div>

                {/* Collapsed: show active-child indicator dot */}
                {!showSidebarLabels && hasActiveChild && (
                  <div className="flex justify-center mt-0.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </div>

      {!showSidebarLabels && collapsedHint && !collapsedPopover && (
        <div
          className="pointer-events-none fixed z-50 hidden rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-[0_20px_50px_-28px_rgba(15,23,42,0.45)] md:block"
          style={{
            left: collapsedHint.left,
            top: collapsedHint.top,
            transform: 'translateY(-50%)',
          }}
        >
          {collapsedHint.label}
        </div>
      )}

      {!showSidebarLabels && collapsedPopover && (
        <div
          ref={collapsedPopoverRef}
          className="fixed z-50 hidden w-72 rounded-2xl border border-slate-800 bg-slate-900 p-2 shadow-[0_20px_50px_-24px_rgba(0,0,0,0.8)] md:block"
          style={{
            left: collapsedPopover.left,
            top: collapsedPopover.top,
            maxHeight: 'calc(100vh - 24px)',
          }}
        >
          <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
            {collapsedPopover.label}
          </div>
          <div className="space-y-1 overflow-y-auto pr-1">
            {collapsedPopover.items.map((item) => (
              <NavLink
                key={item.href}
                to={item.href}
                onClick={() => {
                  setCollapsedPopover(null);
                  setCollapsedHint(null);
                  setMobileMenuOpen(false);
                }}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-all duration-150 ${
                    isActive
                      ? 'bg-orange-500 text-white shadow'
                      : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                  }`
                }
              >
                <span className="text-base leading-none">{item.icon}</span>
                <span className="truncate">{item.label}</span>
              </NavLink>
            ))}
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="min-w-0 flex-1">
        <header className="sticky top-0 z-20 flex min-h-16 flex-wrap items-start gap-3 border-b border-slate-200 bg-white/90 px-3 py-3 backdrop-blur sm:items-center sm:justify-between sm:px-6">
          <div className="min-w-0 flex-1">
            <div className="space-y-1">
              {breadcrumbs.length > 1 && (
                <nav className="flex flex-wrap items-center gap-1 text-xs text-slate-500">
                  {breadcrumbs.map((item, index) => (
                    <React.Fragment key={`${item.label}-${index}`}>
                      {index > 0 && <ChevronRight className="h-3.5 w-3.5 text-slate-300" />}
                      {item.href ? (
                        <Link to={item.href} className="transition hover:text-slate-700">
                          {item.label}
                        </Link>
                      ) : (
                        <span className="font-medium text-slate-700">{item.label}</span>
                      )}
                    </React.Fragment>
                  ))}
                </nav>
              )}
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setMobileMenuOpen(true)} className="md:hidden">
                  <Menu size={16} />
                </Button>
                <h2 className="max-w-[58vw] truncate text-base font-semibold text-gray-900 sm:max-w-none sm:text-lg">{currentTitle}</h2>
              </div>
            </div>
          </div>
          <div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-end sm:gap-3">
            {/* Renders nothing for single-branch users. */}
            <BranchFilterSelect variant="header" />
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefreshAll}
              disabled={refreshing}
            >
              <RefreshCw size={14} className={`sm:mr-1 ${refreshing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">
                {refreshing ? 'Yangilayapti…' : `Yangilash`}
              </span>
            </Button>
            <span className="hidden lg:block text-xs text-slate-400 whitespace-nowrap">
              {lastRefreshed.toLocaleTimeString('uz-UZ', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
            {currentHint && (
              <Button variant="outline" size="sm" onClick={() => setHintOpen(true)}>
                <Info size={14} className="sm:mr-1" /> <span className="hidden sm:inline">Примечание</span>
              </Button>
            )}
            <div className="hidden md:block text-right">
              <p className="text-sm font-medium text-gray-900">{user?.name}</p>
              <p className="text-xs text-gray-500">{user?.roles?.[0] || ''}</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate('/profile')}>
              <UserCircle size={14} className="sm:mr-1" /> <span className="hidden sm:inline">Профиль</span>
            </Button>
            <Button variant="outline" size="sm" onClick={handleLogout}>
              <LogOut size={14} className="sm:mr-1" /> <span className="hidden sm:inline">Выход</span>
            </Button>
          </div>
        </header>

        <div className="overflow-auto p-3 sm:p-5 lg:p-8">
          <Outlet />
        </div>

        {currentHint && (
          <Modal
            isOpen={hintOpen}
            onClose={() => setHintOpen(false)}
            title={currentHint.title}
          >
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Badge variant="default">Примечание</Badge>
                <div className="rounded-full bg-blue-100 p-2 text-blue-700">
                  <Info size={18} />
                </div>
              </div>
              <p className="text-sm text-gray-700 leading-6">{currentHint.description}</p>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                  Что можно делать здесь
                </p>
                <ul className="space-y-2 text-sm text-gray-700">
                  {currentHint.actions.map((action) => (
                    <li key={action} className="flex items-start gap-2">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-blue-500"></span>
                      <span>{action}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Modal>
        )}
      </div>
    </div>
  );
}
